import { type HTMLInputTypeAttribute, type ReactNode } from "react";
import { Field, type FieldProps } from "formik";

import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { useTouchFieldOnSubmit } from "@vega-core/helpers/formik.helper";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

export const DEFAULT_MAX_LENGTH = 400;

export type InputFieldProps = {
  isOptional?: boolean;
  label?: ReactNode | string;
  labelTag?: ReactNode;
  leftLabel?: ReactNode;
  leftLabelClassName?: string;
  hint?: string;
  name: string;
  compact?: boolean;
  onBlur?: VoidFunction;
  tooltipContent?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  inputClassName?: string;
  placeholder?: string;
  warning?: string;
  type?: HTMLInputTypeAttribute;
  showInitialError?: boolean;
  canCopyToAllField?: boolean;
  onCopyAllFields?: (value: string) => void;
  withBorder?: boolean;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
  maxLength?: number;
};

export function InputField({
  isOptional,
  hint,
  label,
  labelTag,
  leftLabel,
  leftLabelClassName,
  name,
  compact,
  onBlur,
  placeholder,
  tooltipContent,
  autoFocus,
  warning,
  disabled,
  inputClassName,
  canCopyToAllField = false,
  type,
  withBorder = true,
  onCopyAllFields,
  fullWidth = false,
  constrainInputWidth = false,
  maxLength = DEFAULT_MAX_LENGTH,
  // TODO: Needed for GP as invalid data gets passed from LP
  // Remove showInitialError prop after all usages are removed
  showInitialError = false,
}: InputFieldProps) {
  useTouchFieldOnSubmit(name);

  return (
    <Field className={cnTwMerge(fullWidth && "w-full")} name={name}>
      {({ field, meta }: FieldProps) => (
        <InputControl
          autoFocus={autoFocus}
          canCopyToAllField={canCopyToAllField}
          compact={compact}
          constrainInputWidth={constrainInputWidth}
          disabled={disabled}
          error={
            (meta.touched || showInitialError) && isTruthy(meta.error)
              ? meta.error
              : undefined
          }
          fullWidth={fullWidth}
          hint={hint}
          inputClassName={inputClassName}
          isOptional={isOptional}
          label={label}
          labelTag={labelTag}
          leftLabel={leftLabel}
          leftLabelClassName={leftLabelClassName}
          maxLength={maxLength}
          name={field.name}
          onBlur={(e) => {
            field.onBlur(e);
            onBlur && onBlur();
          }}
          onChange={field.onChange}
          onCopyAllFields={onCopyAllFields}
          placeholder={placeholder}
          tooltipContent={tooltipContent}
          type={type || "text"}
          value={field.value}
          warning={warning}
          withBorder={withBorder}
        />
      )}
    </Field>
  );
}
